import React, { forwardRef, useContext, useEffect, useImperativeHandle, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { CreateArtifactContext } from "../../../../context/CreateArtifactContext";
import {
  LayoutType,
  SKArtifact,
  SKDocumentArtifact,
  SKListArtifact,
  ContentState,
  getArtefactIcon,
  getContentCreatorActionableItems,
  ContentSubstate,
  TopicTypeString,
} from "../../../../models/SKArtifact";
import { useNavigationBar } from "../../../../context/NavigationContext";
import { capitalizeFirstLetter } from "../../../../utils/stringFunctions";
import { AddContentItem } from "./components/AddContentItem";
import { AddTextField } from "../../../../components/AddTextField";
import { AddTextAreaField } from "../../../../components/AddTextAreaField";
import { AllMediaInput } from "./components/AllMediaInput";
import SelectListLayout from "../SelectListLayout";

import ContentHistoryNavigator from "./components/ContentHistoryNavigator";
import { PublishingTier } from "./components/PublishingTier";
import ContextCheckListCard from "./components/ContextCheckListCard";
import ImageInput from "../ImageInput";
import { ArtifactContainerRef } from "./CreateArtifact";
import ContentEditNavigator from "../../../contentModeration/ContentEditNavigator";
import { useAuth, UserRole } from "../../../../context/AuthContext";
import ContentReportNavigator from "./components/ContentReportNavigator";
import { FolderName } from "../../../../api/shared/uploadService";
//import ImageInput from "../artifacts/create/ImageInput";

const ListArtifactContainer = forwardRef<ArtifactContainerRef>((props, ref) => {
  const [isValidForm, setIsValidForm] = useState(false);
  const context = useAuth();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const { contentData, updateContentData, artifactId, submit, containerButtonTitle, isModerator, isContentEditing, isEditable } =
    useContext(CreateArtifactContext);

  const [error, setErrorState] = useState<{ [key: string]: string | undefined }>({});

  const setErrors = (newErrors: { [key: string]: string | undefined }) => {
    setErrorState(newErrors);
  };

  useImperativeHandle(ref, () => ({
    containerSchema: () => schema,
    setErrors: newErrors => setErrors(newErrors),
  }));
  const navigate = useNavigate();
  const { setNavProps } = useNavigationBar();

  const schema = Yup.object().shape({
    type: Yup.string().required(),
    title: Yup.string().required().max(50),
    subtitle: Yup.string().notRequired().max(500),
    text: Yup.string().notRequired().max(500),
    topic: Yup.string().required(),
    tier: Yup.string().required(),
    age: Yup.number().required(),
    status: Yup.string().required(),
    coverMedia: Yup.object({
      coverImageURL: Yup.string().required("Cover image is required"),
      coverVideoURL: Yup.string().notRequired(),
      coverAudioURL: Yup.string().notRequired(),
    }).required("Cover media is required"),
    certifyFirst: Yup.boolean().oneOf([true]).required("Please certify content"),
    certifySecond: Yup.boolean().oneOf([true]).required("Please certify content"),
    listItems: Yup.array()
      .of(
        Yup.object().shape({
          id: Yup.string().required("ID is required."),
          //listURL: Yup.string().required("Link is required."),
        })
      )
      .required("List items are required.")
      .min(1, "List must contain at least 1 item."),
  });

  useEffect(() => {
    const layout = (contentData as SKListArtifact).layout;
    if (!layout) {
      setShowCreateModal(true);
    }
    switch (layout) {
    }
  }, [contentData]);

  const handleImageChange = (index: number, fileUrl: string | null) => {
    updateContentData({ coverMedia: { ...contentData.coverMedia, coverImageURL: fileUrl! } });
  };

  return (
    <>
      <div className="artifact-page-context">
        {isModerator &&
        !isContentEditing &&
        artifactId &&
        contentData.status === ContentState.ACTIVE &&
        contentData.subStatus === ContentSubstate.EDIT_IN_REVIEW ? (
          <ContentEditNavigator title="Channel Edit" docId={artifactId!} />
        ) : (
          ""
        )}

        {artifactId && (contentData as SKArtifact).hasHistory ? <ContentHistoryNavigator title="Channel Status" contentId={artifactId} /> : ""}
        {artifactId && context?.userRoles?.includes(UserRole.SkarvoAdmin) && contentData.reportCount && contentData.reportCount > 0 ? (
          <ContentReportNavigator title="Content Reports" />
        ) : (
          ""
        )}
        <SelectListLayout isOpen={showCreateModal} onClose={() => setShowCreateModal(false)} />
        <AddContentItem
          index={1}
          onClick={() => {
            navigate("addList");
          }}
          count={(contentData as SKListArtifact)?.listItems ? (contentData as SKListArtifact)?.listItems.length : 0}
        />

        <AddTextField
          name={"addtitle"}
          title={"Add Title"}
          note={"Title can be the subject, purpose, theme or topic of your post."}
          placeholder={"Enter Title"}
          index={2}
          charAllowed={100}
          value={(contentData as SKArtifact).title}
          onChange={async e => {
            updateContentData({ title: e.target.value! });
          }}
          disabled={!isEditable}
          error={error?.title}
        />

        <AddTextAreaField
          name={"addtext"}
          title={"Add Text"}
          note={"Maximum text length of 500 characters."}
          placeholder={"Enter Text"}
          index={3}
          height={150}
          charAllowed={500}
          value={(contentData as SKArtifact).subtitle as string}
          onChange={async e => {
            updateContentData({ subtitle: e.target.value! });
          }}
          disabled={!isEditable}
          error={error?.text}
        />

        {artifactId ? (
          <>
            {/* cover image Upload */}
            <div>
              <div className="relative w-full min-w-[200px]">
                <div className="flex items-center">
                  <span>
                    <span className="content_count">4</span>
                  </span>
                  <span className="content_title">Add Cover Photo</span>
                </div>
                <div className="notes-class-content">
                  Choose a photo that represents the topic, purpose or title of the post. Recommended aspect ratio of photo is either 4:3, 1:1, or
                  3:4.
                </div>
              </div>
              <div>
                <ImageInput
                  key={0}
                  index={4}
                  onFileChange={handleImageChange}
                  docId={artifactId!}
                  maxFileSize={10}
                  imageUrl={contentData.coverMedia?.coverImageURL}
                  editable={isEditable}
                  folder={FolderName.content}
                />
              </div>
            </div>
            {/* cover media Upload */}
            <AllMediaInput
              name={"Add Cover Video or Audio"}
              index={5}
              note={"Choose a video or audio that represents the topic, purpose or title of the post."}
              isEditable={isEditable}
              docId={(contentData as SKDocumentArtifact).id}
            />
          </>
        ) : (
          <></>
        )}

        <ContextCheckListCard
          index={6}
          name="Add Topic"
          title="Choose the primary topic of your Content."
          purposeText="Add Topic"
          notes="Choose the topic that represents the primary focus of most of your Content."
          doneText={contentData.topic ? capitalizeFirstLetter(TopicTypeString[contentData.topic]) : undefined}
          action={() => {
            navigate("selectTopic");
          }}
          disabled={!isEditable}
        />

        <ContextCheckListCard
          index={7}
          name="Add Age Rating"
          title="Choose the Age Rating of your Content"
          purposeText="Add Age Rating"
          notes="Choose the age rating based on the appropriateness of your Content for children."
          doneText={contentData.age ? "Age " + contentData.age + "+" : undefined}
          action={() => {
            navigate("ageRating");
          }}
          disabled={!isEditable}
        />

        <ContextCheckListCard
          index={8}
          name="Certify Content"
          title="Certify Content"
          purposeText="Certify Content"
          notes="Certify that your Content would ensure and comply with these requirements."
          doneText={contentData.certifyFirst && contentData.certifySecond ? "Certified" : undefined}
          action={() => {
            navigate("certifyContent");
          }}
          disabled={!isEditable}
        />
        <PublishingTier index={9} />
      </div>
    </>
  );
});

export default ListArtifactContainer;
